// Copyright (c) Docugami, Inc. All rights reserved.**/

import { Icon, Spinner, Stack } from "@fluentui/react";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { DgProgressBar } from "components/common/DgProgressBar";
import { dgFontFamily } from "components/common/Fonts";
import { isDgFile, isSupportedFile, isSupportedFilePath } from "components/file_management/file_helpers";
import { useFileUploader } from "components/file_management/presentation/FileUploader";
import { GridContainer } from "components/layout/GridContainer";
import { GridItem } from "components/layout/GridItem";
import { DocSourceId } from "components/myContentExplorer/ExplorerTypes";
import { UnsupportedFilesInfo, UnsupportedFilesModal } from "components/myContentExplorer/UnsupportedFilesModal";
import { appBackgroundGrey, emptyGray, greyText, homeGray, lightButtonGray, primaryAccentColor, primaryTheme } from "components/shell/presentation/AppStyles";
import { AddDocsPicker } from "components/storage/AddDocsPicker";
import { useFileCount } from "filemanager/FileCountView";
import { ProviderPickerItem } from "components/storage/ProviderPickerItem";
import { DgFile, FileManagerInstance } from "filemanager/FileManager";
import { FileUploadRequest, FileUploaderInstance } from "filemanager/FileUploader";
import { useDgAuth } from "hooks/DgAuthentication";
import { useDocumentUploadCommand } from "hooks/DocumentUploadCommand";
import { IExplorerStatusItem, useDocSourceGroupStatusItems } from "hooks/ExplorerStatus";
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useAppSelector } from "hooks/redux";
import { useStorageProviderUploadCommand } from "hooks/StorageProviderUploadCommand";
import debounce from "lodash/debounce";
import { GraphItemId, ProviderId, UploadDocumentsRequest } from "networking/StorageProviders";
import { FirstArrivalView, FirstArrivalViewProps } from "pages/FirstArrival";
import { useEffect, useMemo, useState } from "react";
import { StorageManagerInstance } from "storage/StorageManager";
import { getProviderForDocSource, getProviderRootPath } from "storage/StorageProviderHelpers";
import { selectDocSetMinFiles } from "store/organization/organizationSlice";
import styled from "styled-components";
import { FirstArrivalUserUploadCompletedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";

const Container = styled.div`
    width: 100%;
    height: 100%;
    overflow-y: auto;
    overflow-x: auto;
`

const StyledGridContainer = styled(GridContainer)`
    background: ${appBackgroundGrey};
    color: ${homeGray};
    font-family: ${dgFontFamily};
    min-height: 550px;
    min-width: 600px;
`;

const Header = styled.h1`
    text-align: center;
    line-height: 52px;
    font-size: 36px;
    font-weight: 600;
`

const Instructions = styled.div`
    text-align: center;
    line-height: 48px;
    font-size: 24px;
    overflow-wrap: normal;
`

const FileManagementCard = styled.div`
    background: ${primaryTheme.palette.white};
    border-radius: 21px;
    height: 100%;
`

const DropContainer = styled(FileManagementCard) <{ dragging: boolean }>`
    border: 3px dashed ${primaryAccentColor};
    display: flex;
    overflow: auto;
    justify-content: center;
    align-content: center;
    background-color: ${props => props.dragging ? '#EBEAEA' : 'inherit'};
`

const DnDPreviewGrid = styled(GridContainer)`
    width: 360px;
`

const DnDInstructionText = styled.div`
    font-weight: 400;
    font-size: 24px;
    line-height: 48px;
    text-align: center;
    width: 100%;
    color: ${homeGray};
`

const DnDImageContainer = styled.div`
    display: flex;
    align-content: center;
    justify-content: center;
`

const DnDDocImage = styled.img`
    width: 108px;
    height: 141px;
    text-align: center;
    vertical-align: center;
`

const FilesListCard = styled(FileManagementCard)`
    border: 1px solid ${emptyGray};
    font-size: 24px;
    overflow: hidden;
`

const FileListRow = styled.div`
    border-bottom: 1px solid ${emptyGray};
    padding: 10px 41px;
`

const FileListHeader = styled(FileListRow)`
    font-weight: 600;
    font-size: 24px;
    text-transform: uppercase;
    text-align: center;
    height: 36px;
`

const FileList = styled(Stack)`
    height: 90%;
    overflow-y: auto;
`

const StatusText = styled.div`
    text-align: center;
    font-size: 12px;
    font-weight: 800;
    color: ${greyText};
    text-transform: uppercase;
`

const Filename = styled.span`
color: ${homeGray};
    font-weight: 600;
    font-size: 16px;
    width: 50%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
`

const FileStatusText = styled.span`
    font-weight: 400;
    font-size: 16px;
    text-align: center;
    color: ${greyText};
`

const FileStatusContainer = styled.div`
    height: 36px;
    width: 36px;
    border-radius: 18px;
    background: ${lightButtonGray};
    display: flex;
    justify-content: center;
    align-items: center;
`

const StyledAddDocsPicker = styled(AddDocsPicker)`
    display: flex;
    min-height: 320px;
    justify-content: space-around;
`

const SampleSetsText = styled.div`
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
`

const SampleSetsLink = styled.span`
    font-weight: 700;
    cursor: pointer;
`

const NextButtonContainer = styled.div`
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
`

const isGraphItemId = (id: GraphItemId | string): id is GraphItemId => !!(id as GraphItemId).itemId;

const storageProviderRootPaths = () => {
    const rootPaths: string[] = []
    for (const sourceId in DocSourceId) {
        const providerId = getProviderForDocSource(sourceId as DocSourceId);
        if (!!providerId) {
            rootPaths.push(getProviderRootPath(providerId))
        }
    }

    return rootPaths;
};

const dgFileToFile = (dgFile: DgFile): UserFile => {
    return {
        name: dgFile.properties.name,
        id: dgFile.properties.fileId,
        isUploaded: !!dgFile.properties.fileId,
        createdAt: dgFile.properties.createdAt
    }
}

const toSingleGraphItemId = (id: GraphItemId | string) => isGraphItemId(id) ? `${id.itemId}/${id.driveId}` : id;

interface UserFile {
    id?: string;
    path?: string;
    name: string;
    statusText?: string;
    isUploaded?: boolean;
    createdAt: Date;
}

interface FileCountProps {
    fileCount: number;
    minimumDocumentCount?: number;
}

interface HeaderTextProps extends FileCountProps {
    userGivenName?: string;
}

const HeaderText = (props: HeaderTextProps) => {
    if (props.minimumDocumentCount !== undefined && props.fileCount < props.minimumDocumentCount && props.fileCount > 0) {
        return (
            <>
                Looks like you could use a few more docs...
            </>
        )
    }
    else if (props.minimumDocumentCount !== undefined && props.fileCount >= props.minimumDocumentCount) {
        return (
            <>
                Great job! We are now processing your documents...
            </>
        )
    }
    else {
        return (
            <>
                Welcome to Docugami{!!props.userGivenName ? `, ${props.userGivenName}` : ''}!
                <br />
                Let’s get started by creating a doc set.
            </>
        );
    }
};

interface InstructionTextProps extends FileCountProps { }

const InstructionText = (props: InstructionTextProps) => {
    if (props.minimumDocumentCount !== undefined && props.fileCount < props.minimumDocumentCount && props.fileCount > 0) {
        return (
            <>
                Choose a source again and upload at least <b>{props.minimumDocumentCount - props.fileCount}</b> or more docs to complete your doc set.
            </>
        )
    }
    else if (props.minimumDocumentCount !== undefined && props.fileCount >= props.minimumDocumentCount) {
        return (
            <>
                If you have uploaded {props.minimumDocumentCount} or more documents of a similar kind, Docugami will cluster them into a doc set(s) for you. You can upload more docs here or continue.
            </>
        )
    }
    else {
        return (
            <>
                To do this upload <b>{props.minimumDocumentCount} OR MORE DOCUMENTS</b> of a similar
                type like agreements, leases, statements of benefits, etc.
            </>
        );
    }
};

export const UserUploadView = (props: FirstArrivalViewProps) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [files, setFiles] = useState([] as UserFile[]);
    const [dragging, setDragging] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [unsupportedFiles, setUnsupportedFiles] = useState<UnsupportedFilesInfo | null>(null);

    const selectedProviderId = useMemo(() => { return !selectedId ? null : ProviderId[selectedId as keyof typeof ProviderId] }, [selectedId]);

    const minimumDocumentCount = useAppSelector(selectDocSetMinFiles);

    const fileCountValue = useFileCount();
    const fileCount = isLoading || fileCountValue === null ? 0 : fileCountValue;
    const providerUploadCommand = useStorageProviderUploadCommand(selectedProviderId, () => setSelectedId(null), false, onProviderItemsSelected);
    const commands = useMemo(() => [providerUploadCommand], [providerUploadCommand]);
    const [filePicker, dropZone] = useFileUploader('', onLocalItemsSelected, () => { });
    const localDocsUploadCommands = useDocumentUploadCommand(filePicker, () => { });

    const { user } = useDgAuth();
    const navService = useNavigationService();

    const statusItems = useDocSourceGroupStatusItems();

    const debouncedSetDraqgging = debounce((value: boolean) => {
        setDragging(value);
    }, 1000, { leading: true })

    useEffect(() => {
        const getFlattenedFileList = async (paths: string[] = ['', ...storageProviderRootPaths()]): Promise<DgFile[]> => {
            return FileManagerInstance.getFlattenedFileList(paths);
        }

        const updateFiles = () => {
            getFlattenedFileList().then(files => {
                setFiles(files.map(dgFileToFile));
                setIsLoading(false);
            });
        };

        const updateFilesTimeout = setTimeout(updateFiles, 3000);

        const unregisterItemAddedListener = FileManagerInstance.onItemAdded.addListener(newItem => {
            if (isDgFile(newItem)) {
                // Replace the file in the list if it exists
                const addedFile = dgFileToFile(newItem);
                setFiles(currentFiles => {
                    // We optimistically update the files list to show uploading files that don't have an id yet.
                    const updatedFiles = [...currentFiles];
                    let newItemIdx = currentFiles.findIndex(file => file.id === newItem.properties.fileId || (!file.id && file.path === newItem.properties.path));
                    if (newItemIdx === -1) {
                        newItemIdx = updatedFiles.length;
                    }

                    updatedFiles[newItemIdx] = addedFile;
                    return updatedFiles;
                });
            }
            else {
                getFlattenedFileList().then(newFiles => setFiles([...newFiles.map(dgFileToFile)]));
            }
        });

        const unregisterFileUpdatedListener = FileManagerInstance.onFileUpdated.addListener(fileInfo => {
            const [, newProps] = fileInfo;
            const updatedFile = FileManagerInstance.getFile(newProps.path);

            if (!!updatedFile) {
                setFiles(currentFiles => {
                    const fileIndex = currentFiles.findIndex(currentFile => currentFile.id === updatedFile!.properties.fileId || currentFile.path === updatedFile.properties.path);

                    if (fileIndex >= 0) {
                        currentFiles[fileIndex].isUploaded = !!updatedFile!;
                    }

                    return [...currentFiles];
                });
            }
        });

        return () => {
            unregisterItemAddedListener();
            unregisterFileUpdatedListener();
            debouncedSetDraqgging.cancel();
            clearTimeout(updateFilesTimeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedProviderId) {
            return;
        }

        providerUploadCommand.execute();
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedProviderId]);

    const sortedFiles = useMemo(() => files.sort((a, b) => a.name.localeCompare(b.name)), [files]);

    async function onLocalItemsSelected(localItems: FileUploadRequest[]) {
        if (localItems.length === 0) {
            return;
        }

        // filtering out duplicate items and unsupported file types
        const invalidFiles: FileUploadRequest[] = [];
        const validFiles: FileUploadRequest[] = [];
        localItems.forEach(localItem => {
            if (!isSupportedFile(localItem.file.name) || !isSupportedFilePath(localItem.targetPath)) {
                invalidFiles.push(localItem);
            }
            else {
                validFiles.push(localItem);
            }
        });

        // Add the files we are uploading to the list IF it doesn't exist in the list yet.
        setFiles(currentFiles => {
            return [...currentFiles, ...validFiles.filter(validFile => !currentFiles.find(file => file.name === validFile.file.name)).map(validFile => ({
                name: validFile.file.name,
                path: validFile.targetPath,
                isUploaded: false,
                createdAt: new Date()
            } as UserFile))];
        });

        await FileUploaderInstance.upload(validFiles);

        handleUnsupportedFiles(invalidFiles, localItems.length);
    }

    const handleUnsupportedFiles = (unsupportedFiles: FileUploadRequest[] | null, totalCount: number) => {
        if (!unsupportedFiles?.length) {
            setUnsupportedFiles(null);
        }
        else {
            setUnsupportedFiles({
                items: unsupportedFiles.map(file => file.file.name)
            });
        }
    }

    function onProviderItemsSelected(providerItems: ProviderPickerItem[]) {
        if (providerItems.length === 0) {
            return;
        }

        // filtering out duplicate items and unsupported file types
        const invalidItems: ProviderPickerItem[] = [];
        const filteredProviderItems: ProviderPickerItem[] = [];
        providerItems.forEach(p => {
            if (!p.isFolder && !isSupportedFile(p.name)) {
                invalidItems.push(p);
            }
            else if (!files.find(x => x.id === toSingleGraphItemId(p.item.id))) {
                filteredProviderItems.push(p);
            }
        });

        if (filteredProviderItems.length > 0) {
            const remoteItems: { [providerId: string]: UploadDocumentsRequest } = {};

            filteredProviderItems.forEach(pickerItem => {
                const providerId = pickerItem.providerId;
                if (!remoteItems[providerId]) {
                    remoteItems[providerId] = {
                        providerId: providerId,
                        items: []
                    }
                }

                remoteItems[providerId].items.push({
                    item: pickerItem.item,
                    isSynced: !!pickerItem.isSynced
                });
            })

            for (const request of Object.values(remoteItems)) {
                StorageManagerInstance.uploadRequest(request);
            }
        }

        if (invalidItems.length > 0) {
            setUnsupportedFiles({
                items: invalidItems.map(x => x.name)
            });
        }
    }

    const overlays = useMemo(() => [...filePicker.inputElements, ...commands.filter(c => c.isActive && !!c.visual).map(x => x.visual) as JSX.Element[]], [commands, filePicker.inputElements]);

    const docsPickerProps = {
        onProviderItemsSelected: onProviderItemsSelected,
        onLocalItemsSelected: onLocalItemsSelected,
        selectedId: selectedId,
        setSelectedId: setSelectedId,
        localDocsUploadCommands: localDocsUploadCommands,
        overlays: overlays,
        onClose: () => { }
    }

    const dndPreviewProps = {
        onDragOver: (e: React.DragEvent) => {
            dropZone.onDragOver(e);
            debouncedSetDraqgging(true);
        },
        onDragLeaveCapture: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            debouncedSetDraqgging(false);
        }
    }

    const dndContainerProps = {
        onDrop: (e: React.DragEvent): void => {
            dropZone.onDrop(e);
            debouncedSetDraqgging(false);
        },
        onDragEnter: dropZone.onDragEnter
    }

    const hasEnoughFiles = useMemo(() => {
        return minimumDocumentCount !== undefined && (fileCount >= minimumDocumentCount || sortedFiles.filter(file => file.isUploaded).length >= minimumDocumentCount);
    }, [fileCount, minimumDocumentCount, sortedFiles]);

    return (
        <Container
            {...dndPreviewProps}
        >
            <StyledGridContainer
                gap={12}
            >
                <GridItem
                    default={{
                        row: '1',
                        column: '2 / 12'
                    }}
                    md={{
                        row: '2',
                        column: '3 / 11'
                    }}
                >
                    <Header>
                        <HeaderText
                            fileCount={fileCount}
                            minimumDocumentCount={minimumDocumentCount}
                            userGivenName={user?.given_name}
                        />
                    </Header>
                </GridItem>
                <GridItem
                    default={{
                        row: '2',
                        column: '2 / 12'
                    }}
                    md={{
                        row: '3',
                        column: '3 / 11'
                    }}
                >
                    <Instructions
                    >
                        <InstructionText
                            fileCount={fileCount}
                            minimumDocumentCount={minimumDocumentCount}
                        />
                    </Instructions>
                </GridItem>
                <GridItem
                    default={{
                        row: `3 / ${!!files.length || !!statusItems.length ? '7' : '11'}`,
                        column: '2 / 12'
                    }}
                    md={{
                        row: '5 / 11',
                        column: `2 / ${!!files.length || !!statusItems.length ? '7' : '12'}`
                    }}
                    {...dndPreviewProps}
                >
                    <DropContainer
                        {...dndContainerProps}
                        dragging={dragging}
                    >
                        {!dragging ?
                            <StyledAddDocsPicker
                                {...docsPickerProps}
                                iconDimension={50}
                            />
                            :
                            <DnDPreviewGrid
                                numOfRows={4}
                                numOfColumns={3}
                                gap={10}
                            >
                                <GridItem
                                    default={{
                                        row: '2',
                                        column: '1'
                                    }}>
                                    <DnDImageContainer>
                                        <DnDDocImage
                                            src='assets/doc.png'
                                            alt='drag and drop doc' />
                                    </DnDImageContainer>
                                </GridItem>

                                <GridItem
                                    default={{
                                        row: '2',
                                        column: '2'
                                    }}>
                                    <DnDImageContainer>
                                        <DnDDocImage
                                            src='assets/doc.png'
                                            alt='drag and drop doc' />
                                    </DnDImageContainer>
                                </GridItem>
                                <GridItem
                                    default={{
                                        row: '2',
                                        column: '3'
                                    }}>
                                    <DnDImageContainer>
                                        <DnDDocImage
                                            src='assets/doc.png'
                                            alt='drag and drop doc' />
                                    </DnDImageContainer>
                                </GridItem>

                                <GridItem
                                    default={{
                                        row: '3',
                                        column: '1 / 4'
                                    }}>
                                    <DnDInstructionText>
                                        Drop Files Here
                                    </DnDInstructionText>
                                </GridItem>
                            </DnDPreviewGrid>
                        }
                    </DropContainer>
                </GridItem>
                <GridItem
                    default={{
                        hide: !files.length && !statusItems.length,
                        row: '7 / 12',
                        column: '2 / 12'
                    }}
                    md={{
                        row: '5 / 11',
                        column: '7 / 12'
                    }}
                >
                    <Files
                        states={statusItems}
                        files={sortedFiles}
                    />
                </GridItem>
                <GridItem
                    default={{
                        column: '2 / 12',
                        row: '12',
                        hide: hasEnoughFiles
                    }}>
                    <SampleSetsText>
                        <span>To get started using a sample doc set instead, click <SampleSetsLink onClick={() => props.changeView(FirstArrivalView.SampleSets)}>HERE</SampleSetsLink>.</span>
                    </SampleSetsText>
                </GridItem>
                <GridItem
                    default={{
                        column: '2 / 12',
                        row: '11 / 13',
                        hide: !hasEnoughFiles
                    }}>
                    <NextButtonContainer>
                        <DgButton onClick={() => {
                            props.onComplete();
                            setFiles([]);
                            navService.navToLocalDocs(NavigationType.PushNewPage);
                            Telemetry.trackEvent<FirstArrivalUserUploadCompletedSchema>({
                                eventName: TelemetryEvent.FirstArrivalUserUploadCompleted,
                                numOfFiles: fileCount
                            });
                        }} >
                            NEXT
                        </DgButton>
                    </NextButtonContainer>
                </GridItem>
                <UnsupportedFilesModal files={unsupportedFiles || null} onDismiss={() => setUnsupportedFiles(null)} />
            </StyledGridContainer>
        </Container>
    );
}

interface FileListProps {
    files: UserFile[];
    states: IExplorerStatusItem[];
}

const Files = (props: FileListProps) => {
    return !!props.files.length || !!props.states.length ? (
        <FilesListCard>
            <FileListHeader>
                YOUR DOCUMENTS
            </FileListHeader>
            <FileList>
                {
                    props.states.map(status => {
                        return <FileListRow key={status.id}>
                            <Stack
                                horizontalAlign="stretch">
                                <div>
                                    <StatusText>{status.message}</StatusText>
                                    <DgProgressBar
                                        hidden={!status.showProgress}
                                        height={6}
                                        percentComplete={status.progress} />
                                </div>
                            </Stack>
                        </FileListRow>
                    })}
                {
                    props.files.map(file => (
                        <FileListRow key={file.id}>
                            <Stack
                                horizontal
                                horizontalAlign="space-between"
                                verticalAlign="center"
                            >
                                <Filename title={file.name}>{file.name}</Filename>
                                {!!file.statusText ? <FileStatusText>{file.statusText}</FileStatusText> : null}
                                <FileStatusContainer>
                                    {
                                        file.isUploaded ?
                                            <Icon
                                                iconName="CheckMark"
                                            />
                                            :
                                            <Spinner />
                                    }
                                </FileStatusContainer>
                            </Stack>
                        </FileListRow>
                    ))
                }
            </FileList>
        </FilesListCard >
    )
        :
        null;
}
